import React from 'react';
import { usePatternStudio } from '../context/PatternStudioContext';

const ANIMAL_TYPES = [
  'Bear', 'Bunny', 'Cat', 'Dog', 'Fox', 'Elephant', 'Dinosaur',
  'Unicorn', 'Owl', 'Penguin', 'Lion', 'Deer', 'Panda', 'Frog', 'Other',
];

const THEMES = [
  { value: 'minimal',   label: 'Minimal',   desc: 'Clean, neutral tones' },
  { value: 'nursery',   label: 'Nursery',   desc: 'Soft pastels, warm' },
  { value: 'botanical', label: 'Botanical', desc: 'Sage & natural greens' },
  { value: 'classic',   label: 'Classic',   desc: 'Black, white, timeless' },
];

const DIFFICULTIES = ['beginner', 'easy', 'intermediate', 'advanced'];

const DIFF_DESC = {
  beginner:     'No experience needed',
  easy:         'Basic stitches only',
  intermediate: 'Some techniques required',
  advanced:     'Complex shaping & techniques',
};

function Label({ children, required }) {
  return (
    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
      {children} {required && <span className="text-red-400">*</span>}
    </label>
  );
}

function TextInput({ value, onChange, placeholder, className = '' }) {
  return (
    <input
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-stone-400 focus:ring-2 focus:ring-stone-100 transition-all ${className}`}
    />
  );
}

function Textarea({ value, onChange, placeholder, rows = 4 }) {
  return (
    <textarea
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-stone-400 focus:ring-2 focus:ring-stone-100 transition-all resize-none"
    />
  );
}

export default function Step1BasicDetails() {
  const { state, dispatch } = usePatternStudio();
  const p = state.pattern;

  function set(field, value) {
    dispatch({ type: 'SET_FIELD', field, value });
  }

  return (
    <div className="p-6 max-w-xl">
      <h2 className="text-lg font-bold text-gray-900 mb-1">Basic Details</h2>
      <p className="text-sm text-gray-500 mb-6">Tell buyers what this pattern is about.</p>

      <div className="space-y-5">
        {/* Pattern name */}
        <div>
          <Label required>Pattern Name</Label>
          <TextInput
            value={p.title}
            onChange={v => set('title', v)}
            placeholder="e.g. Teddy Bear Amigurumi"
          />
        </div>

        {/* Subtitle */}
        <div>
          <Label>Subtitle</Label>
          <TextInput
            value={p.subtitle}
            onChange={v => set('subtitle', v)}
            placeholder="e.g. Easy beginner crochet pattern"
          />
        </div>

        {/* Description */}
        <div>
          <Label>Description</Label>
          <Textarea
            value={p.description}
            onChange={v => set('description', v)}
            placeholder="Describe your pattern — beginner-friendly, what yarn weight, what the finished amigurumi looks like…"
            rows={4}
          />
        </div>

        {/* Animal type + Finished size row */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label required>Animal Type</Label>
            <select
              value={p.animal_type}
              onChange={e => set('animal_type', e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-stone-400 focus:ring-2 focus:ring-stone-100 transition-all"
            >
              <option value="">Select animal…</option>
              {ANIMAL_TYPES.map(a => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Finished Size</Label>
            <TextInput
              value={p.finished_size}
              onChange={v => set('finished_size', v)}
              placeholder="e.g. 20 cm tall"
            />
          </div>
        </div>

        {/* Gauge / Tension */}
        <div>
          <Label>Gauge / Tension</Label>
          <TextInput
            value={p.gauge || ''}
            onChange={v => set('gauge', v)}
            placeholder="e.g. 14 sc × 16 rows = 10 cm with 3.5 mm hook"
          />
        </div>

        {/* Pattern Notes */}
        <div>
          <Label>Pattern Notes</Label>
          <Textarea
            value={p.pattern_notes || ''}
            onChange={v => set('pattern_notes', v)}
            placeholder="Special notes for crafters — tension tips, yarn weight recommendations, construction method, safety advice for toys…"
            rows={3}
          />
        </div>

        {/* Difficulty */}
        <div>
          <Label required>Difficulty</Label>
          <div className="grid grid-cols-2 gap-2.5">
            {DIFFICULTIES.map(d => (
              <button
                key={d}
                type="button"
                onClick={() => set('difficulty', d)}
                className={`px-4 py-3 rounded-xl border text-left transition-all ${
                  p.difficulty === d
                    ? 'border-stone-400 bg-stone-50'
                    : 'border-gray-200 bg-white hover:border-stone-300'
                }`}
              >
                <div className="text-sm font-semibold text-gray-900 capitalize">{d}</div>
                <div className="text-xs text-gray-400 mt-0.5">{DIFF_DESC[d]}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Language */}
        <div>
          <Label>Crochet Language</Label>
          <div className="flex gap-3">
            {[['us', 'US Terms', 'sc, dc, inc…'], ['uk', 'UK Terms', 'dc, tr, inc…']].map(([val, label, sub]) => (
              <button
                key={val}
                type="button"
                onClick={() => set('language', val)}
                className={`flex-1 px-4 py-3 rounded-xl border text-left transition-all ${
                  p.language === val
                    ? 'border-stone-400 bg-stone-50'
                    : 'border-gray-200 bg-white hover:border-stone-300'
                }`}
              >
                <div className="text-sm font-semibold text-gray-900">{label}</div>
                <div className="text-xs text-gray-400">{sub}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Theme */}
        <div>
          <Label>PDF Theme</Label>
          <div className="grid grid-cols-2 gap-2.5">
            {THEMES.map(t => (
              <button
                key={t.value}
                type="button"
                onClick={() => set('theme', t.value)}
                className={`px-4 py-3 rounded-xl border text-left transition-all ${
                  p.theme === t.value
                    ? 'border-stone-400 bg-stone-50'
                    : 'border-gray-200 bg-white hover:border-stone-300'
                }`}
              >
                <div className="text-sm font-semibold text-gray-900">{t.label}</div>
                <div className="text-xs text-gray-400">{t.desc}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
